// 8 方位のリサイズハンドル用ドラッグハンドラ。
//
// 振る舞い:
// - pointerdown でドラッグ開始（基準位置を捕捉）
// - pointermove でステージ座標差分を方位に応じて x/y/w/h に反映し、対象 DOM を
//   即時プレビュー（doc store/サーバへは送らない）
// - pointerup で最終 x/y/w/h を transformObject op として送信し永続化
//
// 回転対応:
// - object.rotation を受け取り、ポインタ移動量を逆回転 (-θ) して
//   object-local 軸基準のリサイズへ変換する。これにより、回転後の図形でも
//   east ハンドルなら横方向のみが伸び縦方向は変化しない（縦横が連動するバグの修正）。
// - リサイズに伴うローカル中心移動量を世界座標へ +θ で戻して新しい AABB の x/y を求める。
//
// 制約:
// - 最小 16px 制限（潰れ防止）
// - Shift キーでアスペクト比固定はサポートしない（v1 仕様）

import { useEffect, type RefObject } from "react";
import { clampFrameToCanvas } from "@refinement-canvas/domain";
import { CANVAS_SNAP_PX } from "../../../lib/canvas-coordinates";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

export type ResizeDirection = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

interface Options {
  readonly direction: ResizeDirection;
  readonly handleRef: RefObject<HTMLElement | null>;
  readonly objectId: string;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialW: number;
  readonly initialH: number;
  /** 対象オブジェクトの回転角（度）。0 または未指定なら従来通りワールド軸の挙動。 */
  readonly initialRotation?: number | undefined;
  /** 対象オブジェクトの DOM 要素（プレビュー用に left/top/width/height を直接書き換える）。 */
  readonly targetRef: RefObject<HTMLElement | null>;
}

const MIN_SIZE_PX = 16;

export function useResizeHandle(options: Options): void {
  const {
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    targetRef,
  } = options;

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return undefined;

    let dragging: {
      startClientX: number;
      startClientY: number;
    } | null = null;

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      dragging = { startClientX: event.clientX, startClientY: event.clientY };
      handle.setPointerCapture(event.pointerId);
      // 親オブジェクトの drag や stage の pan を起こさせない
      event.stopPropagation();
    };

    const computeResized = (
      clientX: number,
      clientY: number
    ): { x: number; y: number; w: number; h: number } => {
      const view = useCanvasStore.getState().view;
      const worldDx = (clientX - (dragging?.startClientX ?? clientX)) / view.zoom;
      const worldDy = (clientY - (dragging?.startClientY ?? clientY)) / view.zoom;

      // 方位ごとに左/右/上/下のどれを動かすかを決める
      const movesLeft = direction === "nw" || direction === "w" || direction === "sw";
      const movesRight = direction === "ne" || direction === "e" || direction === "se";
      const movesTop = direction === "nw" || direction === "n" || direction === "ne";
      const movesBottom = direction === "sw" || direction === "s" || direction === "se";

      // 世界座標のポインタ移動量をオブジェクト・ローカル軸へ写像する。
      // 回転 θ で描画されている図形に対し、ローカル軸はワールド軸を -θ 回転したもの。
      const rotationDeg = initialRotation ?? 0;
      const rad = (rotationDeg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const localDx = worldDx * cos + worldDy * sin;
      const localDy = -worldDx * sin + worldDy * cos;

      let nextW = initialW;
      let nextH = initialH;
      let centerLocalShiftX = 0;
      let centerLocalShiftY = 0;

      // 横方向: w を変化させ、ローカル X 上で中心が effectiveLocalDx/2 ずれる。
      if (movesLeft) {
        let effectiveLocalDx = localDx;
        let candidateW = initialW - effectiveLocalDx;
        if (candidateW < MIN_SIZE_PX) {
          effectiveLocalDx = initialW - MIN_SIZE_PX;
          candidateW = MIN_SIZE_PX;
        }
        nextW = candidateW;
        centerLocalShiftX = effectiveLocalDx / 2;
      } else if (movesRight) {
        let effectiveLocalDx = localDx;
        let candidateW = initialW + effectiveLocalDx;
        if (candidateW < MIN_SIZE_PX) {
          effectiveLocalDx = MIN_SIZE_PX - initialW;
          candidateW = MIN_SIZE_PX;
        }
        nextW = candidateW;
        centerLocalShiftX = effectiveLocalDx / 2;
      }

      // 縦方向も同様にローカル Y 上で計算する。
      if (movesTop) {
        let effectiveLocalDy = localDy;
        let candidateH = initialH - effectiveLocalDy;
        if (candidateH < MIN_SIZE_PX) {
          effectiveLocalDy = initialH - MIN_SIZE_PX;
          candidateH = MIN_SIZE_PX;
        }
        nextH = candidateH;
        centerLocalShiftY = effectiveLocalDy / 2;
      } else if (movesBottom) {
        let effectiveLocalDy = localDy;
        let candidateH = initialH + effectiveLocalDy;
        if (candidateH < MIN_SIZE_PX) {
          effectiveLocalDy = MIN_SIZE_PX - initialH;
          candidateH = MIN_SIZE_PX;
        }
        nextH = candidateH;
        centerLocalShiftY = effectiveLocalDy / 2;
      }

      // ローカル中心移動量を世界座標へ戻し、AABB の左上座標 (x,y) を更新する。
      const worldCenterShiftX = centerLocalShiftX * cos - centerLocalShiftY * sin;
      const worldCenterShiftY = centerLocalShiftX * sin + centerLocalShiftY * cos;
      const originalCenterX = initialX + initialW / 2;
      const originalCenterY = initialY + initialH / 2;
      const newCenterX = originalCenterX + worldCenterShiftX;
      const newCenterY = originalCenterY + worldCenterShiftY;
      const nextX = newCenterX - nextW / 2;
      const nextY = newCenterY - nextH / 2;

      // リサイズ中もキャンバス境界を越えないよう、最終フレームを必ず有効範囲へ丸める。
      return clampFrameToCanvas({ x: nextX, y: nextY, w: nextW, h: nextH });
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging) return;
      const next = computeResized(event.clientX, event.clientY);
      const target = targetRef.current;
      if (target) {
        target.style.left = `${next.x}px`;
        target.style.top = `${next.y}px`;
        target.style.width = `${next.w}px`;
        target.style.height = `${next.h}px`;
      }
      updateSelectionBoxPreview(next);
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (!dragging) return;
      const next = computeResized(event.clientX, event.clientY);
      dragging = null;
      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
      // グリッド吸着 ON のとき、最終 x/y/w/h を CANVAS_SNAP_PX 単位で丸める。
      const { gridSnapEnabled } = useCanvasStore.getState();
      const finalize = gridSnapEnabled
        ? clampFrameToCanvas({
            x: snapToGrid(next.x),
            y: snapToGrid(next.y),
            w: Math.max(MIN_SIZE_PX, snapToGrid(next.w)),
            h: Math.max(MIN_SIZE_PX, snapToGrid(next.h)),
          })
        : next;
      if (gridSnapEnabled) {
        const target = targetRef.current;
        if (target) {
          target.style.left = `${finalize.x}px`;
          target.style.top = `${finalize.y}px`;
          target.style.width = `${finalize.w}px`;
          target.style.height = `${finalize.h}px`;
        }
        updateSelectionBoxPreview(finalize);
      }
      dispatcher.transformObject(objectId, finalize);
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerUp);
    };
  }, [
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    targetRef,
  ]);
}

function updateSelectionBoxPreview(next: {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  box.style.left = `${next.x}px`;
  box.style.top = `${next.y}px`;
  box.style.width = `${next.w}px`;
  box.style.height = `${next.h}px`;
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}
